import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeFileUrl } from '@/utils/fileUrl';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Wrench, Search, Plus, ChevronLeft, ChevronDown, ChevronRight, ChevronUp, Filter, Building2, Loader2,
  CheckCircle, AlertCircle, Clock, XCircle, AlertTriangle,
  ArrowRightLeft, FileText, Upload, Eye, Trash2, X, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// Gage Type Labels

// Today as YYYY-MM-DD — used to constrain past-only date pickers.
const TODAY_ISO = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();

const GAGE_TYPE_LABELS = {
  'Length': 'Length',
  'Brakes': 'Brakes',
  'Torque': 'Torque',
  'Pressure': 'Pressure',
  'Environmental': 'Environmental',
  'Paint & Coating': 'Paint & Coating',
  'Welding': 'Welding',
  'NDT': 'NDT',
  'Rule 1': 'Rule 1',
  'Service Equipment': 'Service Equipment',
  'Other': 'Other'
};

// Status Labels and Styles
const STATUS_LABELS = {
  'IN_SERVICE': 'In Service',
  'STORAGE': 'Storage',
  'REMOVED_FROM_SERVICE': 'Removed from Service',
  'OUT_FOR_CALIBRATION': 'Out for Calibration',
  'DAMAGED': 'Damaged',
  'UNABLE_TO_LOCATE': 'Unable to Locate',
  'ARCHIVED': 'Archived',
  'DISCARD': 'Discard'
};

const STATUS_STYLES = {
  'IN_SERVICE': 'bg-blue-100 text-blue-700',
  'STORAGE': 'bg-gray-100 text-gray-700',
  'REMOVED_FROM_SERVICE': 'bg-yellow-100 text-yellow-700',
  'OUT_FOR_CALIBRATION': 'bg-sky-100 text-sky-700 ring-1 ring-sky-300',
  'DAMAGED': 'bg-red-100 text-red-700',
  'UNABLE_TO_LOCATE': 'bg-orange-100 text-orange-700',
  'ARCHIVED': 'bg-purple-100 text-purple-700',
  'DISCARD': 'bg-red-100 text-red-800'
};

// Color coding for due date status
const STATUS_COLORS = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  gray: 'bg-gray-400',
  blue: 'bg-sky-500'
};

// Status color dot
function StatusDot({ color }) {
  if (color === 'blue') {
    return (
      <span 
        className="inline-block w-3 h-3 rounded-full bg-sky-500 ring-2 ring-sky-300"
        title="Out for Calibration"
      />
    );
  }
  return (
    <span 
      className={`inline-block w-3 h-3 rounded-full ${STATUS_COLORS[color] || STATUS_COLORS.gray}`}
      title={color}
    />
  );
}

// Status badge
function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_STYLES[status] || STATUS_STYLES.STORAGE}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// Deletion countdown banner
function DeletionBanner({ countdown, status }) {
  if (!countdown) return null;
  
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-red-700">
          <strong>Auto-deletion scheduled:</strong> This gage and its records will be permanently deleted 
          in <strong>{countdown.days_until_deletion} days</strong> (on {countdown.deletion_date}) due to 
          status = {STATUS_LABELS[status] || status}. Export or retain physical records if needed.
        </div>
      </div>
    </div>
  );
}

// Loan indicator
function LoanIndicator({ gage }) {
  if (gage.is_borrowed) {
    return (
      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
        Borrowed from {gage.borrowed_from_facility_name}
      </span>
    );
  }
  if (gage.on_loan) {
    const isOverdue = gage.loan_due_date && new Date(gage.loan_due_date) < new Date();
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
        On loan to {gage.loan_to_facility_name} until {new Date(gage.loan_due_date).toLocaleDateString()}
        {isOverdue && ' (OVERDUE)'}
      </span>
    );
  }
  return null;
}

// Renders the "Due Date" cell value for a gage row.
function GageDueDateCell({ gage }) {
  if (gage.days_remaining === 'daily') {
    return <span className="text-gray-500 italic">Daily</span>;
  }
  if (gage.due_date) {
    return <>{new Date(gage.due_date).toLocaleDateString()}</>;
  }
  return <>-</>;
}

// Renders the "Days Remaining" indicator for a gage row.
function GageDaysRemainingCell({ gage }) {
  if (gage.days_remaining === 'daily') {
    return <span className="text-sm text-gray-500 italic">Daily</span>;
  }
  if (gage.status === 'OUT_FOR_CALIBRATION') {
    return (
      <div className="flex items-center gap-2">
        <StatusDot color="blue" />
        <span className="text-sm text-sky-600">At vendor</span>
      </div>
    );
  }
  if (gage.status === 'IN_SERVICE' && typeof gage.days_remaining === 'number') {
    return (
      <div className="flex items-center gap-2">
        <StatusDot color={gage.status_color} />
        <span className="text-sm">{gage.days_remaining}</span>
      </div>
    );
  }
  return <span className="text-sm text-gray-400">-</span>;
}

// Renders the "Due Date" badge in the gage form (Daily / N/A / Overridden / none).
function DueDateBadge({ isDailyBeforeUse, isReferenceOnly, dueDateManuallySet }) {
  if (isDailyBeforeUse) {
    return <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">Daily</span>;
  }
  if (isReferenceOnly) {
    return <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">N/A</span>;
  }
  if (dueDateManuallySet) {
    return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Overridden</span>;
  }
  return null;
}

// Gage List Component
function GageList({ gages, onSelectGage, selectedFacility, facilities, onFacilityChange, selectedType, onTypeChange, selectedStatus, onStatusChange, loading, onAddGage, userRole }) {
  // 2026-07-19 Phase 1 — Operators/Technicians must never see or select
  // "All Facilities" in the M&T Equipment filter.
  const canPickAllFacilities = userRole === 'ADMIN' || userRole === 'MANAGER';
  const [searchTerm, setSearchTerm] = useState('');
  // 2026-02 — Presentation-only grouping by Type. Tracks collapsed group
  // headers so users can quickly hide types they don't care about. Empty by
  // default = every group is expanded on first render.
  const [collapsedTypes, setCollapsedTypes] = useState(() => new Set());
  const toggleType = (typeKey) => {
    setCollapsedTypes(prev => {
      const next = new Set(prev);
      if (next.has(typeKey)) next.delete(typeKey); else next.add(typeKey);
      return next;
    });
  };

  const filteredGages = gages.filter(gage => {
    const matchesSearch = 
      gage.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gage.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gage.location?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // 2026-02 — Group filtered/sorted rows by their existing Type value.
  // Preserves the current row order within each group (no new sort keys).
  // Groups with zero visible rows are dropped so no empty headers render.
  const groupedByType = (() => {
    const groups = new Map();
    for (const g of filteredGages) {
      const key = g.type || 'Other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(g);
    }
    return Array.from(groups.entries());
  })();
  const TABLE_COL_COUNT = 7;

  return (
    <div className="space-y-4">
      {/* Header with filters — 2026-07-06: Add Gage moved to primary top-right;
          filter row rebalanced with grow/shrink widths. */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="lg:flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Search by description, serial number, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="gage-search"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 lg:w-[520px]">
          {facilities.length > 1 ? (
            <select 
              value={selectedFacility}
              onChange={(e) => onFacilityChange(e.target.value)}
              className="h-10 px-3 border rounded-md text-sm bg-white"
              data-testid="facility-filter"
            >
              {canPickAllFacilities && <option value="">All Facilities</option>}
              {facilities.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          ) : <div />}
          <select 
            value={selectedType}
            onChange={(e) => onTypeChange(e.target.value)}
            className="h-10 px-3 border rounded-md text-sm bg-white"
            data-testid="type-filter"
          >
            <option value="">All Types</option>
            {Object.entries(GAGE_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select 
            value={selectedStatus}
            onChange={(e) => onStatusChange(e.target.value)}
            className="h-10 px-3 border rounded-md text-sm bg-white"
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Gage Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
        </div>
      ) : filteredGages.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No gages found</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full min-w-[900px] table-fixed" data-testid="gage-list-table">
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serial #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Info</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {groupedByType.map(([typeKey, groupRows]) => {
                const isCollapsed = collapsedTypes.has(typeKey);
                const typeLabel = GAGE_TYPE_LABELS[typeKey] || typeKey;
                return (
                  <React.Fragment key={`grp-${typeKey}`}>
                    <tr
                      className="bg-gray-100 hover:bg-gray-200 cursor-pointer border-t border-b border-gray-200"
                      onClick={() => toggleType(typeKey)}
                      data-testid={`gage-type-group-${typeKey}`}
                    >
                      <td colSpan={TABLE_COL_COUNT} className="px-4 py-2.5">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                          {isCollapsed
                            ? <ChevronRight className="w-4 h-4 text-gray-500" />
                            : <ChevronDown className="w-4 h-4 text-gray-500" />}
                          <span>{typeLabel}</span>
                          <span
                            className="text-xs font-normal text-gray-500"
                            data-testid={`gage-type-group-count-${typeKey}`}
                          >
                            · {groupRows.length} equipment item{groupRows.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {!isCollapsed && groupRows.map(gage => (
                      <tr
                        key={gage.id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => onSelectGage(gage)}
                        data-testid={`gage-row-${gage.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 font-medium break-words">{gage.description}</div>
                          {gage.type === 'Other' && gage.type_other_label && (
                            <div className="text-xs text-gray-500 mt-0.5">{gage.type_other_label}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono break-all">{gage.serial_number}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 break-words">{gage.location || '-'}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={gage.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <GageDueDateCell gage={gage} />
                        </td>
                        <td className="px-4 py-3">
                          <GageDaysRemainingCell gage={gage} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <LoanIndicator gage={gage} />
                            {gage.deletion_countdown && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                Deletes in {gage.deletion_countdown.days_until_deletion}d
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Gage Form Component (Create/Edit)
function GageForm({ gage, onSave, onCancel, facilities, api, user }) {
  const isEditing = !!gage?.id;
  const isReadOnly = gage?.is_read_only;
  const userRole = user?.role || 'OPERATOR';
  
  // Determine gage owner options based on role
  const getGageOwnerOptions = () => {
    const options = [];
    if (userRole === 'ADMIN') {
      // Admin can select any facility or organization-wide
      options.push({ value: `ORG_${user?.organization_id}`, label: `${user?.organization_name || 'Organization'} Owned`, type: 'ORGANIZATION' });
      facilities.forEach(f => options.push({ value: `FAC_${f.id}`, label: f.name, type: 'FACILITY' }));
    } else if (userRole === 'MANAGER') {
      // Manager can select from managed facilities
      facilities.forEach(f => options.push({ value: `FAC_${f.id}`, label: f.name, type: 'FACILITY' }));
    } else {
      // Operator - only their assigned facility
      if (facilities.length > 0) {
        options.push({ value: `FAC_${facilities[0].id}`, label: facilities[0].name, type: 'FACILITY' });
      }
    }
    return options;
  };

  const gageOwnerOptions = getGageOwnerOptions();
  const defaultOwner = gageOwnerOptions.length > 0 ? gageOwnerOptions[0].value : '';
  
  const [formData, setFormData] = useState({
    type: gage?.type || 'Length',
    type_other_label: gage?.type_other_label || '',
    description: gage?.description || '',
    serial_number: gage?.serial_number || '',
    gage_owner: gage?.gage_owner_type === 'ORGANIZATION' 
      ? `ORG_${gage?.gage_owner_id}` 
      : `FAC_${gage?.gage_owner_id || gage?.facility_id}`,
    assigned_to: gage?.assigned_to || '',
    gage_range: gage?.gage_range || '',
    calibration_frequency_value: gage?.calibration_frequency_value || '',
    calibration_frequency_unit: gage?.calibration_frequency_unit || 'YEARS',
    due_date_basis: gage?.due_date_basis || 'CALIBRATION_DATE',
    calibration_date: gage?.calibration_date?.split('T')[0] || '',
    in_service_date: gage?.in_service_date?.split('T')[0] || '',
    due_date: gage?.due_date?.split('T')[0] || '',
    calibration_method: gage?.calibration_method || '',
    calibration_results: gage?.calibration_results || '',
    notes: gage?.notes || '',
    status: gage?.status || 'IN_SERVICE',
    status_effective_date: gage?.status_effective_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    status_note: ''
  });
  
  // Static header info (doesn't update until save)
  const [headerInfo] = useState({
    description: gage?.description || '',
    serial_number: gage?.serial_number || ''
  });
  
  // Track if user has manually overridden the due date
  const [dueDateManuallySet, setDueDateManuallySet] = useState(!!gage?.due_date);
  
  const [saving, setSaving] = useState(false);
  const [showLoanDialog, setShowLoanDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCalibVerification, setShowCalibVerification] = useState(!!(gage?.calibration_method || gage?.calibration_results));
  const [loanData, setLoanData] = useState({ loan_to_facility_id: '', loan_due_date: '' });
  const [transferData, setTransferData] = useState({ to_facility_id: '' });
  const [statusModalData, setStatusModalData] = useState({
    status: gage?.status || 'IN_SERVICE',
    status_effective_date: new Date().toISOString().split('T')[0],
    status_note: ''
  });
  const [certificateFile, setCertificateFile] = useState(null);
  const [uploadingCert, setUploadingCert] = useState(false);

  // Check if frequency is "For Reference Only"
  const isReferenceOnly = formData.calibration_frequency_unit === 'FOR_REFERENCE_ONLY';

  // Calculate due date from calibration info
  const calculateDueDate = (freqValue, freqUnit, basis, calDate, inSvcDate) => {
    if (freqUnit === 'FOR_REFERENCE_ONLY' || freqUnit === 'DAILY_BEFORE_USE') return '';
    const basisDate = basis === 'CALIBRATION_DATE' ? calDate : inSvcDate;
    if (!freqValue || !basisDate) return '';
    
    try {
      const date = new Date(basisDate);
      if (isNaN(date.getTime())) return '';
      
      const freq = parseInt(freqValue);
      if (isNaN(freq) || freq <= 0) return '';
      
      if (freqUnit === 'YEARS') {
        date.setFullYear(date.getFullYear() + freq);
      } else if (freqUnit === 'MONTHS') {
        date.setMonth(date.getMonth() + freq);
      } else { // DAYS
        date.setDate(date.getDate() + freq);
      }
      
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Check if frequency is "For Reference Only" or "Daily Before Use"
  const isDailyBeforeUse = formData.calibration_frequency_unit === 'DAILY_BEFORE_USE';
  const skipDueDateCalc = isReferenceOnly || isDailyBeforeUse;

  // Auto-calculate due date when relevant fields change (unless manually set or special frequency)
  useEffect(() => {
    if (!dueDateManuallySet && formData.status === 'IN_SERVICE' && !skipDueDateCalc) {
      const calculated = calculateDueDate(
        formData.calibration_frequency_value,
        formData.calibration_frequency_unit,
        formData.due_date_basis,
        formData.calibration_date,
        formData.in_service_date
      );
      if (calculated) {
        setFormData(prev => prev.due_date !== calculated ? { ...prev, due_date: calculated } : prev);
      }
    }
  }, [
    formData.calibration_frequency_value,
    formData.calibration_frequency_unit,
    formData.due_date_basis,
    formData.calibration_date,
    formData.in_service_date,
    formData.status,
    dueDateManuallySet,
    skipDueDateCalc
  ]);

  const handleChange = (field, value) => {
    // If user changes any calibration field, allow auto-recalculation
    const calibrationFields = ['calibration_frequency_value', 'calibration_frequency_unit', 'due_date_basis', 'calibration_date', 'in_service_date'];
    if (calibrationFields.includes(field)) {
      setDueDateManuallySet(false);
    }
    // Clear due date if switching to FOR_REFERENCE_ONLY or DAILY_BEFORE_USE
    if (field === 'calibration_frequency_unit' && (value === 'FOR_REFERENCE_ONLY' || value === 'DAILY_BEFORE_USE')) {
      setFormData(prev => ({ ...prev, [field]: value, due_date: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDueDateChange = (value) => {
    setFormData(prev => ({ ...prev, due_date: value }));
    setDueDateManuallySet(true); // Mark as manually set
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Parse gage owner
      const [ownerType, ownerId] = formData.gage_owner?.split('_') || ['FAC', ''];
      
      // Prepare payload - clean up empty values
      const payload = { 
        ...formData,
        gage_owner_type: ownerType === 'ORG' ? 'ORGANIZATION' : 'FACILITY',
        gage_owner_id: ownerId
      };
      delete payload.gage_owner; // Remove combined field
      
      // Clean up calibration fields - remove empty values to avoid validation errors
      if (payload.calibration_frequency_value && formData.calibration_frequency_unit !== 'FOR_REFERENCE_ONLY') {
        payload.calibration_frequency_value = parseInt(payload.calibration_frequency_value);
      } else {
        delete payload.calibration_frequency_value; // Remove if empty or reference only
      }
      
      // Remove empty optional fields to avoid validation errors
      if (!payload.calibration_frequency_unit) delete payload.calibration_frequency_unit;
      if (!payload.due_date_basis) delete payload.due_date_basis;
      if (!payload.calibration_date) delete payload.calibration_date;
      if (!payload.in_service_date) delete payload.in_service_date;
      if (!payload.due_date) delete payload.due_date;
      if (!payload.calibration_method) delete payload.calibration_method;
      if (!payload.calibration_results) delete payload.calibration_results;
      if (!payload.notes) delete payload.notes;
      if (!payload.status_note) delete payload.status_note;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.gage_range) delete payload.gage_range;
      if (!payload.type_other_label) delete payload.type_other_label;
      
      if (isEditing) {
        await api.put(`/mt-equipment/gages/${gage.id}`, payload);
        toast.success('Gage updated successfully');
      } else {
        await api.post('/mt-equipment/gages', payload);
        toast.success('Gage created successfully');
      }
      onSave();
    } catch (error) {
      // Bug A fix: Handle both string and array error formats
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save gage';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation errors come as an array
        errorMessage = errorDetail.map(e => e.msg || e.message || 'Validation error').join('; ');
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Bug B fix: State for redirect to edit mode after status change
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const handleStatusChange = async () => {
    // Bug B fix: If changing FROM STORAGE/non-calibration status TO IN_SERVICE
    // and gage doesn't have calibration data, redirect to edit form
    const needsCalibrationData = statusModalData.status === 'IN_SERVICE' && 
      (!gage.calibration_frequency_value || !gage.calibration_date);
    
    if (needsCalibrationData) {
      // Set pending status change and close modal - user needs to fill in calibration fields
      toast.info('Please fill in the calibration fields to complete the status change to In Service.');
      setFormData(prev => ({
        ...prev,
        status: 'IN_SERVICE',
        status_effective_date: statusModalData.status_effective_date,
        status_note: statusModalData.status_note
      }));
      setShowStatusModal(false);
      // The form is already visible since we're editing
      return;
    }
    
    try {
      await api.put(`/mt-equipment/gages/${gage.id}`, {
        status: statusModalData.status,
        status_effective_date: statusModalData.status_effective_date,
        status_note: statusModalData.status_note
      });
      toast.success('Status updated successfully');
      setShowStatusModal(false);
      onSave();
    } catch (error) {
      // Bug A fix: Handle both string and array error formats
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to update status';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        errorMessage = errorDetail.map(e => e.msg || e.message || 'Validation error').join('; ');
      }
      
      toast.error(errorMessage);
    }
  };

  const handleLoan = async () => {
    try {
      await api.post(`/mt-equipment/gages/${gage.id}/loan`, loanData);
      toast.success('Gage loaned successfully');
      setShowLoanDialog(false);
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to loan gage');
    }
  };

  const handleReturn = async () => {
    try {
      await api.post(`/mt-equipment/gages/${gage.id}/return`);
      toast.success('Gage marked as returned');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return gage');
    }
  };

  const handleTransfer = async () => {
    try {
      await api.post(`/mt-equipment/gages/${gage.id}/transfer`, transferData);
      toast.success('Gage transferred successfully');
      setShowTransferDialog(false);
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer gage');
    }
  };

  const handleUploadCertificate = async () => {
    if (!certificateFile) return;
    
    setUploadingCert(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', certificateFile);
      formDataUpload.append('calibration_date', formData.calibration_date || '');
      
      await api.post(`/mt-equipment/gages/${gage.id}/certificates`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Certificate uploaded successfully');
      setCertificateFile(null);
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload certificate');
    } finally {
      setUploadingCert(false);
    }
  };

  const showCalibrationFields = formData.status === 'IN_SERVICE';
  const showStorageFields = formData.status === 'STORAGE';

  return (
    <div className="space-y-6">
      {/* Static Header Bar */}
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onCancel} data-testid="back-to-list">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h2 className="text-xl font-semibold text-gray-900" data-testid="gage-header-title">
                {isEditing ? (headerInfo.description || 'Gage') : 'Add New Gage'}
              </h2>
              {isEditing && headerInfo.serial_number && (
                <p className="text-sm text-gray-500">S/N: {headerInfo.serial_number}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Current Status Display */}
            {isEditing && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Status:</span>
                <StatusBadge status={gage?.status || formData.status} />
              </div>
            )}
            {/* Change Status Button */}
            {isEditing && !isReadOnly && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowStatusModal(true)}
                data-testid="change-status-btn"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Change Status
              </Button>
            )}
            {isEditing && !isReadOnly && (
              <div className="flex items-center gap-2">
                {gage.on_loan ? (
                  <Button variant="outline" size="sm" onClick={handleReturn} data-testid="return-gage-btn">
                    <ArrowRightLeft className="w-4 h-4 mr-1" /> Return
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setShowLoanDialog(true)} data-testid="loan-gage-btn">
                    <ArrowRightLeft className="w-4 h-4 mr-1" /> Loan
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setShowTransferDialog(true)} data-testid="transfer-gage-btn">
                  Transfer
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deletion Banner */}
      {gage?.deletion_countdown && (
        <DeletionBanner countdown={gage.deletion_countdown} status={gage.status} />
      )}

      {/* Borrowed Banner */}
      {isReadOnly && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-purple-700">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm font-medium">
              This gage is borrowed from {gage.borrowed_from_facility_name}. View only.
            </span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Core Fields Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Core Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Gage Owner *</Label>
              <select
                value={formData.gage_owner || defaultOwner}
                onChange={(e) => handleChange('gage_owner', e.target.value)}
                className="w-full h-10 px-3 border rounded-md text-sm bg-white"
                disabled={isReadOnly || userRole === 'OPERATOR'}
                required
                data-testid="gage-owner"
              >
                {gageOwnerOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {userRole === 'OPERATOR' && (
                <p className="text-xs text-gray-500 mt-1">Owner is auto-assigned based on your facility</p>
              )}
            </div>
            <div>
              <Label>Assigned To</Label>
              <Input
                value={formData.assigned_to}
                onChange={(e) => handleChange('assigned_to', e.target.value)}
                placeholder="e.g., Tool Crib A, John Doe's Kit"
                disabled={isReadOnly}
                data-testid="assigned-to"
              />
              <p className="text-xs text-gray-500 mt-1">Specific location or person</p>
            </div>
          </CardContent>
        </Card>

        {/* Gage Information Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Gage Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Gage Type *</Label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="w-full h-10 px-3 border rounded-md text-sm bg-white"
                disabled={isReadOnly}
                required
                data-testid="gage-type"
              >
                {Object.entries(GAGE_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            {formData.type === 'Other' && (
              <div>
                <Label>Other Type Label</Label>
                <Input
                  value={formData.type_other_label}
                  onChange={(e) => handleChange('type_other_label', e.target.value)}
                  placeholder="Specify type"
                  disabled={isReadOnly}
                  data-testid="type-other-label"
                />
              </div>
            )}
            <div className="md:col-span-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Gage description"
                disabled={isReadOnly}
                required
                data-testid="gage-description"
              />
            </div>
            <div>
              <Label>Serial Number *</Label>
              <Input
                value={formData.serial_number}
                onChange={(e) => handleChange('serial_number', e.target.value)}
                placeholder="e.g., ABC-12345"
                maxLength={14}
                disabled={isReadOnly}
                required
                data-testid="serial-number"
              />
            </div>
            <div>
              <Label>Gage Range</Label>
              <Input
                value={formData.gage_range}
                onChange={(e) => handleChange('gage_range', e.target.value)}
                placeholder="e.g., 0-6 inches, 0-100 PSI"
                disabled={isReadOnly}
                data-testid="gage-range"
              />
              <p className="text-xs text-gray-500 mt-1">Measurement range of the gage</p>
            </div>
          </CardContent>
        </Card>

        {/* Status Section - Hidden when editing (use Change Status button instead) */}
        {!isEditing && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Initial Status</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Status *</Label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full h-10 px-3 border rounded-md text-sm bg-white"
                  disabled={isReadOnly}
                  required
                  data-testid="gage-status"
                >
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Status Effective Date {formData.status !== 'IN_SERVICE' ? '*' : ''}</Label>
                <Input
                  type="date"
                  value={formData.status_effective_date}
                  onChange={(e) => handleChange('status_effective_date', e.target.value)}
                  disabled={isReadOnly}
                  required={formData.status !== 'IN_SERVICE'}
                  data-testid="status-effective-date"
                />
              </div>
              <div>
                <Label>Status Note</Label>
                <Input
                  value={formData.status_note}
                  onChange={(e) => handleChange('status_note', e.target.value)}
                  placeholder="Optional note"
                  disabled={isReadOnly}
                  data-testid="status-note"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calibration Information */}
        {showCalibrationFields && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Calibration Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Calibration Frequency {!skipDueDateCalc ? '*' : ''}</Label>
                <div className="flex gap-2">
                  {!skipDueDateCalc && (
                    <Input
                      type="number"
                      min="1"
                      value={formData.calibration_frequency_value}
                      onChange={(e) => handleChange('calibration_frequency_value', e.target.value)}
                      placeholder="e.g., 1"
                      className="w-20"
                      disabled={isReadOnly}
                      required={showCalibrationFields && !skipDueDateCalc}
                      data-testid="freq-value"
                    />
                  )}
                  <select
                    value={formData.calibration_frequency_unit}
                    onChange={(e) => handleChange('calibration_frequency_unit', e.target.value)}
                    className="flex-1 h-10 px-3 border rounded-md text-sm"
                    disabled={isReadOnly}
                    data-testid="freq-unit"
                  >
                    <option value="DAYS">Days</option>
                    <option value="MONTHS">Months</option>
                    <option value="YEARS">Years</option>
                    <option value="FOR_REFERENCE_ONLY">For Reference Only</option>
                    <option value="DAILY_BEFORE_USE">Daily Before Use</option>
                  </select>
                </div>
                {isReferenceOnly && (
                  <p className="text-xs text-amber-600 mt-1">Due date tracking disabled</p>
                )}
                {isDailyBeforeUse && (
                  <p className="text-xs text-sky-600 mt-1">Requires daily verification before use</p>
                )}
              </div>
              {!skipDueDateCalc && (
                <div>
                  <Label>Due Date Basis *</Label>
                  <select
                    value={formData.due_date_basis}
                    onChange={(e) => handleChange('due_date_basis', e.target.value)}
                    className="w-full h-10 px-3 border rounded-md text-sm bg-white"
                    disabled={isReadOnly}
                    required={showCalibrationFields && !skipDueDateCalc}
                    data-testid="due-date-basis"
                  >
                    <option value="CALIBRATION_DATE">Calibration Date</option>
                    <option value="IN_SERVICE_DATE">In-Service Date</option>
                  </select>
                </div>
              )}
              <div>
                <Label>Calibration Date {!skipDueDateCalc && formData.due_date_basis === 'CALIBRATION_DATE' ? '*' : ''}</Label>
                <Input
                  type="date"
                  value={formData.calibration_date}
                  max={TODAY_ISO}
                  onChange={(e) => handleChange('calibration_date', e.target.value)}
                  disabled={isReadOnly}
                  required={showCalibrationFields && !skipDueDateCalc && formData.due_date_basis === 'CALIBRATION_DATE'}
                  data-testid="calibration-date"
                />
              </div>
              <div>
                <Label>In-Service Date {!skipDueDateCalc && formData.due_date_basis === 'IN_SERVICE_DATE' ? '*' : ''}</Label>
                <Input
                  type="date"
                  value={formData.in_service_date}
                  max={TODAY_ISO}
                  onChange={(e) => handleChange('in_service_date', e.target.value)}
                  disabled={isReadOnly}
                  required={showCalibrationFields && !skipDueDateCalc && formData.due_date_basis === 'IN_SERVICE_DATE'}
                  data-testid="in-service-date"
                />
              </div>
              <div className="lg:col-span-4 md:col-span-2">
                <div className="flex items-center gap-2">
                  <Label>Due Date</Label>
                  <DueDateBadge
                    isDailyBeforeUse={isDailyBeforeUse}
                    isReferenceOnly={isReferenceOnly}
                    dueDateManuallySet={dueDateManuallySet}
                  />
                </div>
                {isDailyBeforeUse ? (
                  <p className="text-sm text-sky-600 py-2 font-medium">Daily - Verify calibration before each use</p>
                ) : isReferenceOnly ? (
                  <p className="text-sm text-gray-500 py-2">Due date tracking not applicable for "For Reference Only" gages</p>
                ) : (
                  <div className="flex gap-2 items-start">
                    <div className="flex-1">
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => handleDueDateChange(e.target.value)}
                        disabled={isReadOnly}
                        data-testid="due-date"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-calculated from calibration info; you may override if needed.
                      </p>
                    </div>
                    {dueDateManuallySet && !isReadOnly && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-0"
                        onClick={() => {
                          setDueDateManuallySet(false);
                          const calculated = calculateDueDate(
                            formData.calibration_frequency_value,
                            formData.calibration_frequency_unit,
                            formData.due_date_basis,
                            formData.calibration_date,
                            formData.in_service_date
                          );
                          setFormData(prev => ({ ...prev, due_date: calculated }));
                        }}
                        data-testid="recalculate-due-date"
                      >
                        Recalculate
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calibration Verification Section (Collapsible) */}
        {showCalibrationFields && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Calibration Verification</CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCalibVerification(!showCalibVerification)}
                  data-testid="toggle-calib-verification"
                >
                  {showCalibVerification ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500">Optional calibration verification details</p>
            </CardHeader>
            {showCalibVerification && (
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Calibration Method</Label>
                  <Input
                    value={formData.calibration_method}
                    onChange={(e) => handleChange('calibration_method', e.target.value)}
                    placeholder="Method used for calibration"
                    disabled={isReadOnly}
                    data-testid="calibration-method"
                  />
                </div>
                <div>
                  <Label>Results</Label>
                  <Input
                    value={formData.calibration_results}
                    onChange={(e) => handleChange('calibration_results', e.target.value)}
                    placeholder="Calibration results"
                    disabled={isReadOnly}
                    data-testid="calibration-results"
                  />
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Additional Notes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes..."
              rows={3}
              disabled={isReadOnly}
              data-testid="gage-notes"
            />
          </CardContent>
        </Card>

        {/* Certificates Section (only for editing) */}
        {isEditing && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#005EF8]" />
                Calibration Certificates
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Current Certificate */}
              {gage.certificates && gage.certificates.length > 0 && (
                <div className="mb-4">
                  <Label className="text-sm font-medium mb-2 block">Current Certificate</Label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{gage.certificates[0].file_name}</div>
                      <div className="text-xs text-gray-500">
                        Uploaded {new Date(gage.certificates[0].uploaded_at).toLocaleDateString()}
                      </div>
                    </div>
                    <a 
                      href={normalizeFileUrl(gage.certificates[0].file_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#005EF8] hover:underline text-sm"
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}

              {/* Upload New Certificate */}
              {!isReadOnly && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium mb-2 block">Upload New Certificate</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setCertificateFile(e.target.files[0])}
                      className="flex-1"
                      data-testid="cert-file-input"
                    />
                    <Button 
                      type="button"
                      onClick={handleUploadCertificate}
                      disabled={!certificateFile || uploadingCert}
                      data-testid="upload-cert-btn"
                    >
                      {uploadingCert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {/* Certificate History */}
              {gage.certificates && gage.certificates.length > 1 && (
                <div className="mt-4 border-t pt-4">
                  <Label className="text-sm font-medium mb-2 block">Certificate History</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {gage.certificates.slice(1).map(cert => (
                      <div key={cert.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="flex-1">{cert.file_name}</span>
                        <span className="text-gray-500">{new Date(cert.uploaded_at).toLocaleDateString()}</span>
                        <a 
                          href={normalizeFileUrl(cert.file_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#005EF8] hover:underline"
                        >
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Status History (only for editing) */}
        {isEditing && gage.status_history && gage.status_history.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Status History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {gage.status_history.map(entry => (
                  <div key={entry.id} className="flex items-start gap-3 p-2 bg-gray-50 rounded text-sm">
                    <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <div>
                        {entry.old_status ? (
                          <>
                            <StatusBadge status={entry.old_status} />
                            <span className="mx-2">→</span>
                          </>
                        ) : null}
                        <StatusBadge status={entry.new_status} />
                      </div>
                      {entry.note && <div className="text-gray-600 mt-1">{entry.note}</div>}
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(entry.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Buttons */}
        {!isReadOnly && (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-gage-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isEditing ? 'Update Gage' : 'Create Gage'}
            </Button>
          </div>
        )}
      </form>

      {/* Loan Dialog */}
      {showLoanDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Loan Gage</h3>
            <div className="space-y-4">
              <div>
                <Label>Loan to Facility *</Label>
                <select
                  value={loanData.loan_to_facility_id}
                  onChange={(e) => setLoanData(prev => ({ ...prev, loan_to_facility_id: e.target.value }))}
                  className="w-full h-10 px-3 border rounded-md text-sm bg-white"
                  data-testid="loan-facility-select"
                >
                  <option value="">Select facility</option>
                  {facilities.filter(f => f.id !== gage.facility_id).map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Loan Due Date *</Label>
                <Input
                  type="date"
                  value={loanData.loan_due_date}
                  onChange={(e) => setLoanData(prev => ({ ...prev, loan_due_date: e.target.value }))}
                  data-testid="loan-due-date"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowLoanDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleLoan}
                disabled={!loanData.loan_to_facility_id || !loanData.loan_due_date}
                data-testid="confirm-loan-btn"
              >
                Confirm Loan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Dialog */}
      {showTransferDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Transfer Gage</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently transfer ownership of this gage and all its records to another facility.
            </p>
            <div>
              <Label>Transfer to Facility *</Label>
              <select
                value={transferData.to_facility_id}
                onChange={(e) => setTransferData({ to_facility_id: e.target.value })}
                className="w-full h-10 px-3 border rounded-md text-sm bg-white"
                data-testid="transfer-facility-select"
              >
                <option value="">Select facility</option>
                {facilities.filter(f => f.id !== gage.facility_id).map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleTransfer}
                disabled={!transferData.to_facility_id}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-transfer-btn"
              >
                Confirm Transfer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Change Gage Status</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowStatusModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Current Status */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-500 mb-1">Current Status</div>
              <StatusBadge status={gage?.status} />
            </div>

            <div className="space-y-4">
              <div>
                <Label>New Status *</Label>
                <select
                  value={statusModalData.status}
                  onChange={(e) => setStatusModalData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full h-10 px-3 border rounded-md text-sm bg-white"
                  data-testid="status-modal-status"
                >
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Effective Date *</Label>
                <Input
                  type="date"
                  value={statusModalData.status_effective_date}
                  onChange={(e) => setStatusModalData(prev => ({ ...prev, status_effective_date: e.target.value }))}
                  required
                  data-testid="status-modal-date"
                />
              </div>
              <div>
                <Label>Status Note</Label>
                <Textarea
                  value={statusModalData.status_note}
                  onChange={(e) => setStatusModalData(prev => ({ ...prev, status_note: e.target.value }))}
                  placeholder="Optional reason for status change..."
                  rows={2}
                  data-testid="status-modal-note"
                />
              </div>
            </div>

            {/* Status History Summary */}
            {gage?.status_history && gage.status_history.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <Label className="text-sm font-medium mb-2 block">Recent Status Changes</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {gage.status_history.slice(0, 3).map(entry => (
                    <div key={entry.id} className="text-xs p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-1">
                        {entry.old_status && <><StatusBadge status={entry.old_status} /><span>→</span></>}
                        <StatusBadge status={entry.new_status} />
                      </div>
                      <div className="text-gray-400 mt-1">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
              <Button 
                onClick={handleStatusChange}
                disabled={!statusModalData.status || !statusModalData.status_effective_date || statusModalData.status === gage?.status}
                data-testid="confirm-status-change-btn"
              >
                Update Status
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main M&T Equipment Page
export default function MTEquipmentPage() {
  const { api, user } = useAuth();
  const [gages, setGages] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedGage, setSelectedGage] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch facilities
      const facilitiesRes = await api.get('/mt-equipment/facilities');
      setFacilities(facilitiesRes.data || []);

      // Build query params
      const params = new URLSearchParams();
      if (selectedFacility) params.append('facility_id', selectedFacility);
      if (selectedType) params.append('gage_type', selectedType);
      if (selectedStatus) params.append('status', selectedStatus);

      // Fetch gages
      const gagesRes = await api.get(`/mt-equipment/gages?${params.toString()}`);
      setGages(gagesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api, selectedFacility, selectedType, selectedStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectGage = async (gage) => {
    try {
      // Fetch full gage details
      const res = await api.get(`/mt-equipment/gages/${gage.id}`);
      setSelectedGage(res.data);
    } catch (error) {
      toast.error('Failed to load gage details');
    }
  };

  const handleSave = () => {
    setSelectedGage(null);
    setIsCreating(false);
    fetchData();
  };

  const handleCancel = () => {
    setSelectedGage(null);
    setIsCreating(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mt-equipment-page">
      {/* Page Header */}
      {!selectedGage && !isCreating && (
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#005EF8]/10 rounded-lg">
              <Wrench className="w-6 h-6 text-[#005EF8]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">M &amp; T Equipment</h1>
              <p className="text-sm text-gray-500">Manage and track measuring &amp; testing equipment calibrations</p>
            </div>
          </div>
          {/* 2026-07-06 — Primary action moved from filter row to top-right. */}
          <Button
            onClick={() => setIsCreating(true)}
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white shrink-0"
            data-testid="add-gage-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Gage
          </Button>
        </div>
      )}

      {/* Content */}
      {selectedGage || isCreating ? (
        <GageForm 
          gage={selectedGage}
          onSave={handleSave}
          onCancel={handleCancel}
          facilities={facilities}
          api={api}
          user={user}
        />
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-4 h-4 text-[#005EF8]" />
              Equipment List
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                {gages.length} total
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <GageList 
              gages={gages}
              onSelectGage={handleSelectGage}
              selectedFacility={selectedFacility}
              facilities={facilities}
              onFacilityChange={setSelectedFacility}
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              loading={loading}
              onAddGage={() => setIsCreating(true)}
              userRole={user?.role}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
